import { useState } from 'react';
import type { FetchPetFormValues, Species } from './types';

export const PET_NAME_MAX_LENGTH = 30;

// A fixed run of asterisks rather than one per character, so the mask does not
// leak the length of the address.
const MASK = '******';

// r******@narvar.com -- keeps the first character and the domain, per Fetch's
// post-UAT request. Display only: the real address is what we validate and send.
export function maskEmail(email: string): string {
  const at = email.lastIndexOf('@');
  if (at < 1) return email ? MASK : '';
  return `${email[0]}${MASK}${email.slice(at)}`;
}

interface UsePetFormArgs {
  loading?: boolean;
  initialEmail?: string;
  initialZip?: string;
  initialValues?: FetchPetFormValues | null;
}

export function usePetForm({
  loading,
  initialEmail,
  initialZip,
  initialValues,
}: UsePetFormArgs) {
  const [species, setSpeciesState] = useState<Species>(
    initialValues?.species ?? 'DOG',
  );
  const [petName, setPetNameState] = useState(
    (initialValues?.petName ?? '').slice(0, PET_NAME_MAX_LENGTH),
  );
  // Fetch asked for no preset breed or age: an unpicked select has to stay
  // unpicked rather than quietly quoting whatever happens to sort first.
  const [breedId, setBreedId] = useState(initialValues?.breedId ?? '');
  const [age, setAge] = useState(initialValues?.age ?? '');
  const [zip, setZip] = useState(initialValues?.zip ?? initialZip ?? '');
  const [email, setEmailState] = useState(
    initialValues?.email ?? initialEmail ?? '',
  );
  // An address we already know is shown masked and never unmasked. Editing
  // clears the field so the shopper types a replacement instead of revealing
  // what was there.
  const [emailMasked, setEmailMasked] = useState(() => !!email);
  const [emailEditing, setEmailEditing] = useState(false);

  const setEmail = (next: string) => {
    setEmailMasked(false);
    setEmailEditing(false);
    setEmailState(next);
  };

  // Focus empties what is *displayed*, not the address itself. The field is
  // blank and editable before any character arrives, so no keystroke is
  // swallowed -- and because `email` is untouched, validation never drops out
  // from under a shopper who focused the field and changed nothing. Clearing
  // the state here instead would disable the submit button while the field has
  // focus, and a disabled button does not blur the input that would restore it.
  const beginEmailEdit = () => {
    if (emailMasked) setEmailEditing(true);
  };

  // Nothing typed, so the mask simply comes back.
  const endEmailEdit = () => setEmailEditing(false);

  const setPetName = (next: string) =>
    setPetNameState(next.slice(0, PET_NAME_MAX_LENGTH));

  // A dog breed is meaningless once the shopper switches to a cat, so the
  // selection clears rather than jumping to another species' first entry. The
  // toggle fires on every click including the already-selected one, so an
  // unchanged species must leave the chosen breed alone.
  const setSpecies = (next: Species) => {
    if (next === species) return;
    setSpeciesState(next);
    setBreedId('');
  };

  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
  const zipValid = /^\d{5}$/.test(zip.trim());
  const canSubmit =
    !loading &&
    petName.trim().length > 0 &&
    breedId !== '' &&
    age !== '' &&
    zipValid &&
    emailValid;

  const getValues = (): FetchPetFormValues => ({
    species,
    petName: petName.trim(),
    breedId,
    age,
    zip: zip.trim(),
    email: email.trim(),
  });

  return {
    species,
    setSpecies,
    petName,
    setPetName,
    breedId,
    setBreedId,
    age,
    setAge,
    zip,
    setZip,
    email,
    setEmail,
    emailMasked,
    emailDisplay: emailMasked ? (emailEditing ? '' : maskEmail(email)) : email,
    beginEmailEdit,
    endEmailEdit,
    canSubmit,
    getValues,
  };
}
